import React, { useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  Modal,
  Popconfirm,
  Progress,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useBranding } from '@/theme/useBranding';
import { DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  PollDto,
  useCreatePoll,
  useDeletePoll,
  usePolls,
  useUpdatePoll,
} from '@shared/api/hooks/usePolls';

interface OptionRow {
  labelIt: string;
  labelEn?: string;
}

interface CreateValues {
  questionIt: string;
  questionEn?: string;
  options: OptionRow[];
  closeAt?: Dayjs;
  isActive: boolean;
  sendStartNotification?: boolean;
  notificationTitle?: string;
  notificationSubtitle?: string;
}

interface EditValues {
  questionIt: string;
  questionEn?: string;
  closeAt?: Dayjs;
  isActive: boolean;
}

/** Anteprima semplice di come apparirà la push sul telefono (mock statico). */
function NotificationPreview({
  title,
  subtitle,
}: {
  title?: string;
  subtitle?: string;
}): React.ReactElement {
  const b = useBranding();
  return (
    <div style={{ marginTop: 4 }}>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        Anteprima sul telefono
      </Typography.Text>
      <div
        style={{
          background: '#f0f2f5',
          borderRadius: 16,
          padding: 12,
          marginTop: 6,
          maxWidth: 340,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ width: 20, height: 20, borderRadius: 5, background: b.primary }} />
          <Typography.Text style={{ fontSize: 11, textTransform: 'uppercase', color: '#8c8c8c' }}>
            {b.clubName || 'Lanciano FC'} · ora
          </Typography.Text>
        </div>
        <div style={{ marginTop: 6 }}>
          <Typography.Text strong style={{ display: 'block' }}>
            {title?.trim() || 'Nuovo sondaggio'}
          </Typography.Text>
          <Typography.Text style={{ color: '#595959' }}>
            {subtitle?.trim() || 'Dì la tua: vota ora nella Fanzone'}
          </Typography.Text>
        </div>
      </div>
    </div>
  );
}

export function PollsPage(): React.ReactElement {
  const [createOpen, setCreateOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [createForm] = Form.useForm<CreateValues>();
  const [editForm] = Form.useForm<EditValues>();

  const { data: polls = [], isLoading, refetch, isFetching } = usePolls(true);
  const createMutation = useCreatePoll();
  const updateMutation = useUpdatePoll();
  const deleteMutation = useDeletePoll();

  // Anteprima reattiva della notifica di avvio.
  const notifOn = Form.useWatch('sendStartNotification', createForm);
  const notifTitle = Form.useWatch('notificationTitle', createForm);
  const notifSubtitle = Form.useWatch('notificationSubtitle', createForm);

  const openCreate = (): void => {
    createForm.resetFields();
    createForm.setFieldsValue({
      isActive: true,
      options: [{ labelIt: '' }, { labelIt: '' }],
      sendStartNotification: false,
    });
    setCreateOpen(true);
  };

  const openEdit = (p: PollDto): void => {
    setEditId(p.id);
    editForm.setFieldsValue({
      questionIt: p.question.it ?? '',
      questionEn: p.question.en,
      closeAt: p.closeAt ? dayjs(p.closeAt) : undefined,
      isActive: p.isActive,
    });
  };

  const onCreate = async (): Promise<void> => {
    const v = await createForm.validateFields();
    const options = (v.options ?? [])
      .filter((o) => o.labelIt && o.labelIt.trim())
      .map((o, i) => ({
        label: { it: o.labelIt, ...(o.labelEn ? { en: o.labelEn } : {}) },
        position: i,
      }));
    if (options.length < 2) {
      message.error('Servono almeno 2 opzioni');
      return;
    }
    try {
      await createMutation.mutateAsync({
        question: {
          it: v.questionIt,
          ...(v.questionEn ? { en: v.questionEn } : {}),
        },
        options,
        closeAt: v.closeAt ? v.closeAt.toISOString() : undefined,
        isActive: v.isActive,
        sendStartNotification: v.sendStartNotification ?? false,
        ...(v.sendStartNotification && v.notificationTitle
          ? { notificationTitle: v.notificationTitle }
          : {}),
        ...(v.sendStartNotification && v.notificationSubtitle
          ? { notificationSubtitle: v.notificationSubtitle }
          : {}),
      });
      message.success(
        v.sendStartNotification ? 'Sondaggio creato e notifica inviata' : 'Sondaggio creato',
      );
      setCreateOpen(false);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onEdit = async (): Promise<void> => {
    if (!editId) return;
    const v = await editForm.validateFields();
    try {
      await updateMutation.mutateAsync({
        id: editId,
        input: {
          question: {
            it: v.questionIt,
            ...(v.questionEn ? { en: v.questionEn } : {}),
          },
          closeAt: v.closeAt ? v.closeAt.toISOString() : undefined,
          isActive: v.isActive,
        },
      });
      message.success('Sondaggio aggiornato');
      setEditId(null);
    } catch (e) {
      const err = e as { response?: { data?: { message?: string | string[] } } };
      const msg = err.response?.data?.message;
      message.error(Array.isArray(msg) ? msg.join('; ') : (msg ?? 'Errore'));
    }
  };

  const onDelete = async (id: string): Promise<void> => {
    try {
      await deleteMutation.mutateAsync(id);
      message.success('Sondaggio eliminato');
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } };
      message.error(err.response?.data?.message ?? 'Errore');
    }
  };

  return (
    <Card
      title={
        <Space>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Sondaggi
          </Typography.Title>
          <Tag>{polls.length}</Tag>
        </Space>
      }
      extra={
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
            Aggiorna
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Nuovo sondaggio
          </Button>
        </Space>
      }
    >
      <Table<PollDto>
        rowKey="id"
        loading={isLoading}
        dataSource={polls}
        pagination={false}
        scroll={{ x: 'max-content' }}
        expandable={{
          expandedRowRender: (r) => (
            <Space direction="vertical" style={{ width: '100%' }}>
              {r.options.map((o) => {
                const pct = r.totalVotes ? Math.round((o.voteCount / r.totalVotes) * 100) : 0;
                return (
                  <div key={o.id}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Typography.Text>{o.label.it ?? '(sin label)'}</Typography.Text>
                      <Typography.Text type="secondary">
                        {o.voteCount} voti · {pct}%
                      </Typography.Text>
                    </Space>
                    <Progress percent={pct} showInfo={false} size="small" />
                  </div>
                );
              })}
            </Space>
          ),
          rowExpandable: () => true,
        }}
        columns={[
          {
            title: 'Domanda',
            render: (_v, r) => (
              <Typography.Text strong>{r.question.it ?? '(senza domanda)'}</Typography.Text>
            ),
          },
          {
            title: 'Opzioni',
            width: 80,
            align: 'center',
            render: (_v, r) => <Tag>{r.options.length}</Tag>,
          },
          {
            title: 'Voti totali',
            dataIndex: 'totalVotes',
            width: 100,
            align: 'right',
            render: (v: number) => <Typography.Text strong>{v}</Typography.Text>,
          },
          {
            title: 'Chiusura',
            dataIndex: 'closeAt',
            width: 160,
            render: (v: string | null) =>
              v ? dayjs(v).format('DD/MM/YYYY HH:mm') : <Typography.Text type="secondary">—</Typography.Text>,
          },
          {
            title: 'Stato',
            dataIndex: 'isActive',
            width: 100,
            render: (_v, r) => {
              const closed = r.closeAt && dayjs(r.closeAt).isBefore(dayjs());
              if (closed) return <Tag color="default">Chiuso</Tag>;
              return r.isActive ? (
                <Tag color="green">Attivo</Tag>
              ) : (
                <Tag color="orange">Inattivo</Tag>
              );
            },
          },
          {
            title: '',
            width: 100,
            align: 'right',
            render: (_v, r) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
                <Popconfirm
                  title="Eliminare sondaggio?"
                  okText="Elimina"
                  cancelText="Annulla"
                  okButtonProps={{ danger: true }}
                  onConfirm={() => onDelete(r.id)}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      {/* Create */}
      <Modal
        title="Nuovo sondaggio"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={onCreate}
        okText="Crea"
        confirmLoading={createMutation.isPending}
        destroyOnHidden
        width={640}
      >
        <Form form={createForm} layout="vertical" preserve={false}>
          <Form.Item
            name="questionIt"
            label="Domanda (IT)"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={2} placeholder="Chi sara MVP?" />
          </Form.Item>
          <Form.Item name="questionEn" label="Domanda (EN)">
            <Input.TextArea rows={2} />
          </Form.Item>

          <Typography.Text strong>Opzioni (2-10)</Typography.Text>
          <Form.List name="options">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%', marginTop: 8 }}>
                {fields.map(({ key, name }) => (
                  <Space key={key} align="baseline" style={{ width: '100%' }}>
                    <Form.Item
                      name={[name, 'labelIt']}
                      rules={[{ required: true }]}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="Opzione IT" />
                    </Form.Item>
                    <Form.Item
                      name={[name, 'labelEn']}
                      style={{ marginBottom: 0, flex: 1 }}
                    >
                      <Input placeholder="Opzione EN (opt)" />
                    </Form.Item>
                    {fields.length > 2 ? (
                      <Button
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    ) : null}
                  </Space>
                ))}
                {fields.length < 10 ? (
                  <Button onClick={() => add({ labelIt: '' })} icon={<PlusOutlined />} block>
                    Aggiungi opzione
                  </Button>
                ) : null}
              </Space>
            )}
          </Form.List>

          <Form.Item name="closeAt" label="Chiusura (opzionale)" style={{ marginTop: 16 }}>
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="isActive" label="Attivo subito" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Form.Item
            name="sendStartNotification"
            label="Notifica di avvio"
            valuePropName="checked"
            style={{ marginBottom: 8 }}
            tooltip="Manda una push ai tifosi quando il sondaggio parte. Se spento, il sondaggio parte comunque senza notifica."
          >
            <Switch checkedChildren="Invia" unCheckedChildren="No" />
          </Form.Item>
          {notifOn ? (
            <>
              <Form.Item
                name="notificationTitle"
                label="Titolo notifica"
                rules={[{ required: true, message: 'Titolo obbligatorio', max: 100 }]}
              >
                <Input placeholder="Es. Vota il MVP" maxLength={100} />
              </Form.Item>
              <Form.Item name="notificationSubtitle" label="Sottotitolo" rules={[{ max: 180 }]}>
                <Input placeholder="Es. Scegli il migliore in campo" maxLength={180} />
              </Form.Item>
              <NotificationPreview title={notifTitle} subtitle={notifSubtitle} />
            </>
          ) : null}
        </Form>
      </Modal>

      {/* Edit */}
      <Modal
        title="Modifica sondaggio (no opzioni)"
        open={editId !== null}
        onCancel={() => setEditId(null)}
        onOk={onEdit}
        okText="Salva"
        confirmLoading={updateMutation.isPending}
        destroyOnHidden
        width={520}
      >
        <Form form={editForm} layout="vertical" preserve={false}>
          <Form.Item
            name="questionIt"
            label="Domanda (IT)"
            rules={[{ required: true }]}
          >
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="questionEn" label="Domanda (EN)">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="closeAt" label="Chiusura">
            <DatePicker
              showTime
              format="DD/MM/YYYY HH:mm"
              disabledDate={(d) => d.isBefore(dayjs().startOf('day'))}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="isActive" label="Attivo" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Typography.Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
            Per modificare le opzioni, eliminare il sondaggio e ricrearlo.
          </Typography.Text>
        </Form>
      </Modal>
    </Card>
  );
}
